<div class="container mx-auto px-4 py-8">
  <div class="mb-8 text-center">
    <h1 class="mb-3 text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100 md:text-4xl">MCP Servers</h1>
    <p class="mx-auto max-w-2xl text-gray-600 dark:text-gray-300">
      Explore a curated list of MCP servers, showcasing their features, popularity, and community engagement. Find the
      perfect server for your needs and contribute to the MCP ecosystem.
    </p>
  </div>

  <!-- Centered Search Input -->
  <div class="flex justify-center mb-6">
    <div class="relative w-full max-w-md">
      <label hlmLabel for="serverSearch" class="sr-only">Search Servers</label>
      <input
        hlmInput
        id="serverSearch"
        class="pl-10 w-full pr-10"
        placeholder="Search MCP servers..."
        [formControl]="searchInputControl"
      />
      <ng-icon name="lucideSearch" class="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
      @if (searchInputControl.value) {
        <button
          hlmBtn
          variant="ghost"
          size="icon"
          class="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 rounded-full text-muted-foreground hover:text-foreground"
          (click)="clearSearch()"
          aria-label="Clear search"
        >
          <ng-icon hlm name="lucideX" size="sm" />
        </button>
      }
    </div>
  </div>

  <!-- Search results message when searching -->
  @if (search() && servers().length > 0) {
    <div class="mb-6 text-center">
      <p class="text-sm text-gray-600 dark:text-gray-400">
        Showing results for "<span class="font-medium">{{ search() }}</span
        >"
        <button hlmBtn variant="ghost" size="sm" class="ml-2 h-8 px-2 py-0" (click)="clearSearch()">Clear</button>
      </p>
    </div>
  }

  <!-- Filters and Sort Controls - mobile responsive -->
  <div class="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-6">
    <!-- Server Type Filter Buttons -->
    <div class="flex flex-wrap gap-2">
      <button
        hlmBtn
        [variant]="typeFilter() === 'all' ? 'default' : 'outline'"
        size="sm"
        (click)="onTypeFilterChange('all')"
      >
        All
      </button>
      <button
        hlmBtn
        [variant]="typeFilter() === 'official' ? 'default' : 'outline'"
        size="sm"
        (click)="onTypeFilterChange('official')"
      >
        <ng-icon name="lucideBadgeCheck" class="mr-1.5" />
        Official
      </button>
      <button
        hlmBtn
        [variant]="typeFilter() === 'archived' ? 'default' : 'outline'"
        size="sm"
        (click)="onTypeFilterChange('archived')"
      >
        <ng-icon name="lucideArchive" class="mr-1.5" />
        Archived
      </button>
      <button
        hlmBtn
        [variant]="typeFilter() === 'community' ? 'default' : 'outline'"
        size="sm"
        (click)="onTypeFilterChange('community')"
      >
        <ng-icon name="lucideUsers" class="mr-1.5" />
        Community
      </button>
    </div>

    <!-- Sort Select -->
    <div class="w-full sm:w-auto sm:min-w-[200px]">
      <hlm-select class="w-full" [formControl]="sortControl">
        <hlm-select-trigger class="w-full">
          <hlm-select-value />
        </hlm-select-trigger>
        <hlm-select-content>
          @for (option of sortOptions; track option.value) {
            <hlm-option [value]="option.value">{{ option.label }}</hlm-option>
          }
        </hlm-select-content>
      </hlm-select>
    </div>
  </div>

  @if (loading()) {
    <div class="flex justify-center items-center h-64">
      <app-loading-spinner />
    </div>
  } @else if (error()) {
    <app-error-message [message]="error()!" (retry)="loadServers(page(), pageSize(), sort(), search())" />
  } @else if (servers().length === 0) {
    <div class="py-10 text-center">
      <ng-icon hlm name="lucidePocketKnife" size="xl" class="mb-4 inline-block text-muted-foreground" />
      <p class="text-xl text-muted-foreground">No MCP servers found matching your criteria.</p>
      @if (search()) {
        <p class="mt-2 text-muted-foreground">Try adjusting your search or clearing the filter.</p>
        <button hlmBtn variant="outline" class="mt-4" (click)="clearSearch()">Clear Search</button>
      }
    </div>
  } @else {
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      @for (server of servers(); track server.id) {
        <a
          [href]="server.url"
          target="_blank"
          rel="noopener noreferrer"
          class="block relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg dark:border-gray-700 dark:bg-gray-900 dark:hover:shadow-gray-700/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
        >
          <div class="p-5 flex flex-col h-full">
            <div class="flex items-center justify-between">
              <h2
                class="truncate text-lg font-medium leading-tight text-gray-900 dark:text-gray-100"
                [title]="server.name"
              >
                @if (search()) {
                  <span [innerHTML]="server.name | highlightSearch: search() || ''"></span>
                } @else {
                  {{ server.name }}
                }
              </h2>

              <div class="flex items-center space-x-3">
                <span
                  hlmBadge
                  variant="secondary"
                  class="font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 pointer-events-none capitalize"
                  [ngClass]="{
                    '!bg-green-100 !text-green-800 dark:!bg-green-900 dark:!text-green-300': server.type === 'official',
                    '!bg-red-100 !text-red-800 dark:!bg-red-900 dark:!text-red-300': server.type === 'archived',
                  }"
                >
                  <ng-icon
                    [name]="
                      server.type === 'official'
                        ? 'lucideBadgeCheck'
                        : server.type === 'archived'
                          ? 'lucideArchive'
                          : 'lucideUsers'
                    "
                    class="mr-1.5"
                  />
                  {{ server.type }}
                </span>
              </div>
            </div>

            <span class="mb-3 inline-flex items-center text-xs text-gray-500 dark:text-gray-400">
              <!-- <ng-icon name="lucideGithub" class="mr-1.5" size="14" /> -->
              by&nbsp;
              <span>{{ getRepoOwner(server.url || '') }}</span>
            </span>

            <p class="mb-4 line-clamp-3 text-sm text-gray-600 dark:text-gray-300">
              @if (search()) {
                <span [innerHTML]="server.summary || server.description || '' | highlightSearch: search() || ''"></span>
              } @else {
                {{ server.summary || server.description }}
              }
            </p>
            <div class="flex items-center justify-between mt-auto">
              <div class="flex space-x-4">
                <div class="flex items-center text-sm" hlmTooltip="GitHub Stars">
                  <ng-icon name="lucideStar" class="mr-1.5 text-yellow-500" />
                  <span class="text-xs text-black dark:text-white">{{ formatNumber(server.stars) }}</span>
                </div>
                <div class="flex items-center text-sm" hlmTooltip="Downloads">
                  <ng-icon name="lucideDownload" class="mr-1.5 text-blue-500" />
                  <span class="text-xs text-black dark:text-white">{{ formatNumber(server.downloads, '--') }}</span>
                </div>
              </div>
              <button hlmBtn [variant]="'outline'" [size]="'xsm'">
                Visit Source
                <ng-icon hlm [size]="'xs'" [name]="'lucideExternalLink'" class="ml-1" />
              </button>
            </div>
          </div>
        </a>
      }
    </div>

    <div class="mt-8 flex justify-center">
      <hlm-numbered-pagination
        [totalItems]="totalItems()"
        [currentPage]="page()"
        [itemsPerPage]="pageSize()"
        [maxSize]="responsiveMaxSize()"
        [pageSizes]="[12, 24, 60, 120]"
        (currentPageChange)="onPageChange($event)"
        (itemsPerPageChange)="onPageSizeChange($event)"
      />
    </div>
  }
</div>
